import React, { useState, useEffect } from 'react';
import { Calendar, Camera, Video, Phone, Package, Clock, MapPin, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function UpcomingSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/auth/client/upcoming-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('No se pudieron cargar las sesiones');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return Camera;
      case 'video': return Video;
      case 'call': return Phone;
      case 'delivery': return Package;
      default: return Calendar;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'photo': return 'from-pink-500 to-rose-500';
      case 'video': return 'from-purple-500 to-violet-500';
      case 'call': return 'from-blue-500 to-cyan-500';
      case 'delivery': return 'from-orange-500 to-amber-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.getTime() === today.getTime()) {
        return 'Hoy';
      } else if (date.getTime() === tomorrow.getTime()) {
        return 'Manana';
      } else {
        return date.toLocaleDateString('es-MX', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long' 
        });
      }
    } catch {
      return dateStr;
    }
  };

  const addToCalendar = (session) => {
    const startDate = session.date.replace(/-/g, '');
    const startTime = (session.time || '10:00').replace(':', '') + '00';
    
    // Calculate end time (add 2 hours for photo/video, 1 hour for calls)
    const durationMinutes = session.type === 'photo' || session.type === 'video' ? 120 : 60;
    const [hours, mins] = (session.time || '10:00').split(':').map(Number);
    const endHours = hours + Math.floor((mins + durationMinutes) / 60);
    const endMins = (mins + durationMinutes) % 60;
    const endTime = `${String(endHours).padStart(2, '0')}${String(endMins).padStart(2, '0')}00`;
    
    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(session.title)}&dates=${startDate}T${startTime}/${startDate}T${endTime}&ctz=America/Mexico_City${session.location ? `&location=${encodeURIComponent(session.location)}` : ''}`;
    
    window.open(gcalUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#f47c2a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-[#AEAEB2]">
        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-[#6E6E73]" />
        <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Sin sesiones programadas</h3>
        <p className="text-[#AEAEB2]">Cuando tengas citas programadas, aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-[#1D1D1F] flex items-center gap-2">
          <Calendar className="w-6 h-6 text-[#f47c2a]" />
          Proximas Sesiones
        </h2>
        <span className="text-sm text-[#AEAEB2]">{sessions.length} programada(s)</span>
      </div>

      <div className="space-y-3">
        {sessions.map((session) => {
          const Icon = getTypeIcon(session.type);
          const colorClass = getTypeColor(session.type);
          
          return (
            <div 
              key={session.id}
              className="bg-white/50 rounded-xl border border-[rgba(0,0,0,0.06)]/50 overflow-hidden hover:border-[rgba(0,0,0,0.08)] transition-colors"
            >
              <div className="flex items-stretch">
                {/* Color bar with icon */}
                <div className={`w-20 flex-shrink-0 bg-gradient-to-b ${colorClass} flex items-center justify-center`}>
                  <Icon className="w-8 h-8 text-[#1D1D1F]" />
                </div>
                
                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#1D1D1F] text-lg truncate">
                        {session.title}
                      </h3>
                      <p className="text-sm text-[#f47c2a] font-medium mt-0.5">
                        {session.type_label}
                        {session.business_name && ` • ${session.business_name}`}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#AEAEB2]">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span className="capitalize">{formatDate(session.date)}</span>
                        </div>
                        {session.time && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            <span>{session.time} hrs</span>
                          </div>
                        )}
                        {session.location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{session.location}</span>
                          </div>
                        )}
                      </div>
                      
                      {session.description && (
                        <p className="text-sm text-[#AEAEB2] mt-2 line-clamp-2">
                          {session.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Add to calendar button */}
                    <button
                      onClick={() => addToCalendar(session)}
                      className="flex-shrink-0 px-4 py-2 bg-gray-700 hover:bg-white/60 text-[#1D1D1F] rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      <span className="hidden sm:inline">Agregar</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
